import { useEffect, useLayoutEffect, useRef, useCallback, useState } from 'react';

interface UseAutoScrollResult {
  containerRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  showScrollBtn: boolean;
  scrollToBottom: () => void;
  onScrollBtnClick: () => void;
  releasePin: () => void;
}

/**
 * A remembered scroll position. We persist both a raw pixel `scrollTop` and an
 * element anchor (the index of the top-most visible transcript row plus how far
 * its top sits above the viewport). The anchor is what makes restoration robust
 * when rows use `content-visibility: auto`: off-screen rows report an *estimated*
 * height, so a bare pixel offset no longer maps to the same content between
 * save and restore. Re-anchoring to a specific row keeps that row in the same
 * place regardless of how the estimated heights above it shift. `anchorIndex`
 * is `-1` when no anchor could be computed (e.g. jsdom has no layout, or the
 * content had no children yet), in which case restore falls back to `scrollTop`.
 */
interface RememberedScroll {
  scrollTop: number;
  pinned: boolean;
  anchorIndex: number;
  anchorDelta: number;
}

type ScrollAnchor = { index: number; delta: number };

const scrollMemory = new Map<string, RememberedScroll>();

/**
 * Find the top-most transcript row that is at or above the viewport's top edge
 * and how far its top sits above that edge. Direct children of the content
 * element are the anchor units. Returns `null` when there are no children or
 * none is at/above the top (e.g. jsdom, which reports zero-size rects).
 */
function computeScrollAnchor(container: HTMLElement, content: HTMLElement): ScrollAnchor | null {
  const children = content.children;
  if (children.length === 0) return null;
  const containerTop = container.getBoundingClientRect().top;
  const scrollTop = container.scrollTop;
  let best: ScrollAnchor | null = null;
  for (let i = 0; i < children.length; i++) {
    const childTopInViewport = children[i].getBoundingClientRect().top - containerTop;
    const childScrollTop = scrollTop + childTopInViewport;
    if (childScrollTop <= scrollTop + 1) {
      best = { index: i, delta: scrollTop - childScrollTop };
    } else {
      // Children are laid out top-to-bottom; the first one below the viewport
      // top means every later child is below it too.
      break;
    }
  }
  return best;
}

/**
 * Manages auto-scroll-to-bottom behaviour for a scrollable container.
 *
 * The scroll position is "pinned" to the bottom by default. Once the user
 * scrolls up, the pin is released and the floating scroll-to-bottom button
 * surfaces. The pin is restored when the user scrolls back to the bottom
 * themselves, or when they click the floating button.
 *
 * A ResizeObserver on both `contentRef` and `containerRef` keeps the view
 * pinned when new content is appended or the container resizes (e.g. when an
 * input below the scroll area grows/shrinks). Corrections are applied
 * synchronously inside the observer callback (which runs after layout but
 * before paint), so no frame is ever painted at a stale offset — on a
 * session switch the whole transcript arrives in one commit, and a deferred
 * correction would flash (or, under streaming load, linger at) the top of
 * the transcript.
 *
 * Distinguishing user-driven scrolls from content-growth scrolls is the
 * tricky part. A naive "user is interacting" heuristic (wheel/pointerdown
 * etc.) is unreliable: `wheel` has no matching end event, so a stray
 * trackpad scroll leaves the flag stuck on, and any subsequent scroll-event
 * (including the programmatic `scrollToBottom` we issue from the
 * ResizeObserver) is misread as a user scroll — which silently unpins the
 * view as new events stream in.
 *
 * Instead we watch `scrollHeight` between scroll events:
 *   - If `scrollHeight` grew since the previous scroll event, the position
 *     change was driven by new content (or our own snap-to-bottom), so we
 *     leave the pin alone.
 *   - Otherwise the user moved the viewport, so update the pin from
 *     `isAtBottom()`.
 *
 * Usage:
 *   const { containerRef, contentRef, showScrollBtn, onScrollBtnClick } = useAutoScroll();
 *   // attach containerRef to the scrollable outer div
 *   // attach contentRef to the inner content div
 */
export function useAutoScroll(options?: {
  initiallyPinned?: boolean;
  /**
   * When set, the hook remembers `scrollTop` and pinned state across unmount
   * keyed by this string. Remounting with the same key restores the position;
   * a pinned-at-bottom user stays pinned (and sees any newly arrived content).
   * Content often renders asynchronously after remount, so the restore is
   * retried on every content-size change until the saved offset is reachable
   * (or the user scrolls, which cancels it).
   */
  storageKey?: string;
}): UseAutoScrollResult {
  const storageKey = options?.storageKey;
  const saved = storageKey ? scrollMemory.get(storageKey) : undefined;
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(saved ? !saved.pinned : false);
  const pinnedRef = useRef(saved ? saved.pinned : (options?.initiallyPinned ?? true));
  const lastScrollHeightRef = useRef(0);
  // Saved position still waiting to be applied. Content usually renders async
  // after mount, so a one-shot restore would get clamped to 0 by the browser;
  // instead we keep re-applying on content growth until the position is
  // reachable, or the user scrolls and takes over. Carries both the pixel
  // offset and the element anchor (see RememberedScroll).
  const pendingRestoreRef = useRef<{
    scrollTop: number;
    anchorIndex: number;
    anchorDelta: number;
  } | null>(
    saved && !saved.pinned
      ? {
          scrollTop: saved.scrollTop,
          anchorIndex: saved.anchorIndex,
          anchorDelta: saved.anchorDelta,
        }
      : null,
  );
  // Target of the most recent programmatic restore whose scroll event has not
  // been observed yet; lets `handleScroll` tell our own restore scrolls apart
  // from genuine user scrolls.
  const restoreTargetRef = useRef<number | null>(null);
  // Live copy of the container's scroll offset and anchor, maintained from
  // scroll events. Fallback for the unmount save when the DOM can no longer be
  // trusted (node already detached, or content never laid out): a detached
  // element (no CSS box) reports scrollTop 0, and reading it would corrupt
  // the saved position.
  const liveScrollTopRef = useRef(saved?.scrollTop ?? 0);
  const liveAnchorRef = useRef<ScrollAnchor | null>(
    saved && saved.anchorIndex >= 0 ? { index: saved.anchorIndex, delta: saved.anchorDelta } : null,
  );

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const isAtBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return true;
    return el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
  }, []);

  const applyPendingRestore = useCallback(() => {
    const el = containerRef.current;
    const pending = pendingRestoreRef.current;
    if (!el || pending === null) return;

    // Prefer the element anchor when the referenced row exists: it keeps that
    // row in the same viewport position even when `content-visibility` makes
    // the estimated heights above it differ from when the position was saved.
    // Fall back to the raw pixel offset otherwise (row not loaded yet, or no
    // layout — e.g. jsdom).
    const content = contentRef.current;
    const anchorRow =
      pending.anchorIndex >= 0 && content
        ? (content.children[pending.anchorIndex] as HTMLElement | undefined)
        : undefined;
    let target: number;
    if (anchorRow) {
      const childTopInViewport =
        anchorRow.getBoundingClientRect().top - el.getBoundingClientRect().top;
      target = el.scrollTop + childTopInViewport + pending.anchorDelta;
    } else {
      target = pending.scrollTop;
    }

    const max = Math.max(0, el.scrollHeight - el.clientHeight);
    const clamped = Math.min(Math.max(0, target), max);
    if (el.scrollTop !== clamped) {
      // Mark the upcoming scroll event as ours so it is not misread as a user
      // scroll (which would cancel the restore and flip the pin state). Only
      // armed when the assignment actually moves the viewport — a clamped
      // no-op fires no scroll event, and a stale marker would swallow the
      // next genuine user scroll. Set before assigning: the event may
      // dispatch synchronously.
      restoreTargetRef.current = clamped;
      el.scrollTop = clamped;
    }
    // Done once the content is tall enough to actually reach the position;
    // until then the browser clamps scrollTop and we retry on the next grow.
    if (max >= target) {
      pendingRestoreRef.current = null;
    }
  }, []);

  // Position the view before the first paint: re-apply a saved offset, or
  // start a pinned session at the bottom instead of the top. Content usually
  // grows after mount (events load and stream in asynchronously); the
  // ResizeObserver below keeps correcting until the position settles.
  useLayoutEffect(() => {
    if (pendingRestoreRef.current !== null) {
      applyPendingRestore();
    } else if (pinnedRef.current) {
      scrollToBottom();
    }
  }, [applyPendingRestore, scrollToBottom]);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    lastScrollHeightRef.current = container.scrollHeight;

    const handleScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const heightChanged = el.scrollHeight !== lastScrollHeightRef.current;
      lastScrollHeightRef.current = el.scrollHeight;

      const restoreTarget = restoreTargetRef.current;
      if (restoreTarget !== null) {
        restoreTargetRef.current = null;
        const max = Math.max(0, el.scrollHeight - el.clientHeight);
        // Position matches the (clamped) restore target: it's our own scroll.
        // Otherwise the user moved first — fall through to the user path.
        if (el.scrollTop === Math.min(restoreTarget, max)) {
          liveScrollTopRef.current = el.scrollTop;
          return;
        }
      }
      // Known limitation: a user scroll that lands on the same frame as
      // content growth is attributed to the growth and does not cancel a
      // pending restore (nor update the pin) — inherent to the height-delta
      // heuristic above. The next isolated scroll event corrects it. Height
      // changes also skip the live-offset capture: a shrink-clamp scroll
      // (content collapsing) must not overwrite the real position.
      if (heightChanged) return;

      liveScrollTopRef.current = el.scrollTop;
      liveAnchorRef.current = content ? computeScrollAnchor(el, content) : null;
      // A genuine user scroll takes over from any in-flight restore.
      pendingRestoreRef.current = null;
      const atBottom = isAtBottom();
      pinnedRef.current = atBottom;
      setShowScrollBtn(!atBottom);
    };

    const handleResize = () => {
      lastScrollHeightRef.current = container.scrollHeight;
      // Correct the offset synchronously: ResizeObserver callbacks run after
      // layout but before paint, so the frame that grew the content is
      // painted already scrolled to the right place. Deferring to rAF
      // painted that frame at the stale offset — after a session switch that
      // offset is the top of the transcript, and while events stream in the
      // correction can lag several frames behind. The deferred scroll's
      // stale-offset scroll event could also be misread as a user scroll,
      // silently unpinning the view or cancelling the restore. Setting
      // scrollTop never resizes the observed elements, so this cannot
      // re-trigger the observer.
      if (pendingRestoreRef.current !== null) {
        applyPendingRestore();
        return;
      }
      if (!pinnedRef.current) return;
      scrollToBottom();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(content);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [applyPendingRestore, isAtBottom, scrollToBottom, storageKey]);

  // Save the position for `storageKey` in the layout-phase cleanup: it runs
  // while the node is still in the document, so the DOM reports real
  // geometry. Reading the DOM (rather than the scroll-event-mirrored refs)
  // matters because scroll events dispatch asynchronously — a scroll made
  // moments before switching sessions has not produced its event yet, and
  // the event dies with the detached node. The refs would save the
  // pre-scroll position and pin state; the DOM has the truth.
  useLayoutEffect(() => {
    if (!storageKey) return;
    return () => {
      // A still-pending restore (content never grew tall enough to apply
      // it) outranks everything: the container sits at a clamped offset
      // that says nothing about where the user actually was, so persist the
      // pending target unchanged.
      const pending = pendingRestoreRef.current;
      if (pending !== null) {
        scrollMemory.set(storageKey, {
          scrollTop: pending.scrollTop,
          pinned: false,
          anchorIndex: pending.anchorIndex,
          anchorDelta: pending.anchorDelta,
        });
        return;
      }
      const el = containerRef.current;
      const content = contentRef.current;
      if (el && el.isConnected && el.scrollHeight > el.clientHeight) {
        const pinned = isAtBottom();
        const anchor = !pinned && content ? computeScrollAnchor(el, content) : null;
        scrollMemory.set(storageKey, {
          scrollTop: el.scrollTop,
          pinned,
          anchorIndex: anchor?.index ?? -1,
          anchorDelta: anchor?.delta ?? 0,
        });
        return;
      }
      // Detached or never-laid-out container: fall back to the mirrored refs.
      scrollMemory.set(storageKey, {
        scrollTop: liveScrollTopRef.current,
        pinned: pinnedRef.current,
        anchorIndex: liveAnchorRef.current?.index ?? -1,
        anchorDelta: liveAnchorRef.current?.delta ?? 0,
      });
    };
  }, [isAtBottom, storageKey]);

  const onScrollBtnClick = useCallback(() => {
    // Cancel any in-flight restore explicitly: if the view already sits at
    // the clamped max, scrollToBottom fires no scroll event, so the user-path
    // cancellation in handleScroll never runs — and the next resize would
    // yank the view back to the saved offset against the user's click.
    pendingRestoreRef.current = null;
    pinnedRef.current = true;
    setShowScrollBtn(false);
    scrollToBottom();
  }, [scrollToBottom]);

  /**
   * Hand the viewport to a caller that is about to scroll it somewhere of its
   * own choosing (a jump to an anchor, say). The height-delta heuristic in
   * `handleScroll` cannot release the pin for such a scroll: moving up through
   * `content-visibility` rows renders them at their real heights, so every
   * scroll event it produces reports a height change and is attributed to
   * content growth. Left pinned, the next resize snaps the view back to the
   * bottom and the jump appears to do nothing.
   */
  const releasePin = useCallback(() => {
    pendingRestoreRef.current = null;
    pinnedRef.current = false;
    if (!isAtBottom()) setShowScrollBtn(true);
  }, [isAtBottom]);

  return { containerRef, contentRef, showScrollBtn, scrollToBottom, onScrollBtnClick, releasePin };
}
